import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import { positionDropdown } from '../../../common/dropdown'
import { UseTimeZone } from '../../../common/hooks/useTimeZone'
import { TextLink } from '../../../common/html/TextLink'
import { Button } from '../../../common/html/button'
import { Element } from '../../../common/html/element'
import { PopupContactUs } from '../../../common/order/popup/popupContactUs'
import { PopupExistedSubscription } from '../../../common/popup/popupExistedSubscription'
import { Tooltip } from '../../../common/tooltip'
import { FORMAT, formatDateTime } from '../../../helpers/datetime'
import { selectUserType } from '../../../layout/store/slice'
import { USER_TYPE } from '../../../utils/authorization'
import { EMPTY_VALUE } from '../../../utils/common'
import classes from '../../index.module.scss'
import { remainingDays, remainingMonthsAndDays } from '../common/helper'
import { PopupUpgrade } from '../common/popupUpgrade'
import {
  selectIsAvailableToAddNewSubscription,
  selectSubscriptionInfo,
} from '../store/slice'
import {
  getIsAvailableToAddNewSubscription,
  getUserSetting,
} from '../store/thunk'
import { PopupCancel } from './PopupCancelTrial'
import { PopupTrialNow } from './PopupTrialNow'

export const YourSubscription = () => {
  const dispatch = useDispatch()

  const timeZone = UseTimeZone()

  const userType = useSelector(selectUserType)
  const subscriptionInfo = useSelector(selectSubscriptionInfo)
  const additionNewSubscriptionData = useSelector(
    selectIsAvailableToAddNewSubscription,
  )

  const locale = useSelector((state) => state.i18n.locale)

  const [isShowViewPlan, setIsShowViewPlan] = useState(false)
  const [isShowPopupTrialNow, setIsShowPopupTrialNow] = useState(false)
  const [isOpenPopupContactUs, setIsOpenPopupContactUs] = useState(false)
  const [openPopupExistedSubscription, setOpenPopupExistedSubscription] =
    useState(false)
  const [isShowPopupCancel, setIsShowPopupCancel] = useState(false)

  const isBasicPlan = userType.userTypeId === USER_TYPE.BASIC
  const isLitePlan = userType.userTypeId === USER_TYPE.LITE
  const isTrialPlan = userType.userTypeId === USER_TYPE.TRIAL
  const isStandardPlan = userType.userTypeId === USER_TYPE.STANDARD
  const isPremiumPlan = userType.userTypeId === USER_TYPE.PREMIUM

  const isBasicOrLitePlan = isBasicPlan || isLitePlan
  const isPremiumOrStandard = isStandardPlan || isPremiumPlan

  const showContactUsPopup = () => {
    setIsOpenPopupContactUs(true)
  }

  const onClosePopupContactUs = () => {
    setIsOpenPopupContactUs(false)
  }

  const showViewPlan = () => {
    setIsShowViewPlan(true)
  }

  useEffect(() => {
    dispatch(getUserSetting())
  }, [locale, userType])

  useEffect(() => {
    dispatch(getIsAvailableToAddNewSubscription())
  }, [])

  return (
    <>
      <Element
        className={classes.subscriptionContent}
        style={{ backgroundColor: '#F3F3F3' }}
      >
        <div className={classes.subscriptionInfo}>
          <Element
            style={{ fontSize: 16 }}
            className={classes.subscriptionTitle}
          >
            <Translate value="subscriptionPayment.YOUR_SUBSCRIPTION" />
          </Element>

          <div className={classes.fieldInfo}>
            <div className={classes.fieldName}>
              <Translate value="subscriptionPayment.YOUR_CURRENT_PLAN" />
            </div>
            <div
              className={[classes.fieldValue, 'fw600'].join(' ')}
              style={{ color: '#3DA967', fontSize: 16 }}
            >
              {subscriptionInfo?.currentPlan}
            </div>
          </div>

          {isPremiumOrStandard ? (
            <div className={classes.fieldInfo}>
              <div className={classes.fieldName}>
                <Translate value="subscriptionPayment.SUBSCRIPTION_TERM" />
              </div>
              <div className={classes.fieldValue}>
                {subscriptionInfo?.months
                  ? `${subscriptionInfo?.months} ${I18n.t(
                      'subscriptionPayment.MONTHS',
                    )}`
                  : EMPTY_VALUE}
              </div>
            </div>
          ) : (
            <></>
          )}

          {userType && isBasicOrLitePlan ? (
            <></>
          ) : (
            <div className={classes.fieldInfo}>
              <div className={classes.fieldName}>
                <Translate value="subscriptionPayment.EXPIRY_DATE" />
              </div>
              <div className={classes.fieldValue}>
                {formatDateTime(
                  subscriptionInfo?.expiryDate,
                  FORMAT.DATE_TIME,
                  locale,
                  timeZone,
                )}
                &nbsp; (<Translate value="subscriptionPayment.REMAINING" />
                &nbsp;
                {isTrialPlan
                  ? remainingDays(
                      new Date(),
                      new Date(subscriptionInfo?.expiryDate),
                    )
                  : remainingMonthsAndDays(
                      new Date(),
                      new Date(subscriptionInfo?.expiryDate),
                    )}
                )
              </div>
            </div>
          )}

          <div className={classes.fieldInfo}>
            {isTrialPlan ? (
              <TextLink
                id={'subscriptionPaymentViewPlan'}
                isTextDecoration={false}
                onClick={showContactUsPopup}
                value="subscriptionPayment.PROMOTION_CONTEXT_TRIAL"
                labelLink="subscriptionPayment.PROMOTION_CONTEXT_LINK_TRIAL"
              />
            ) : (
              <TextLink
                id={'subscriptionPaymentViewPlan'}
                isTextDecoration={false}
                onClick={showViewPlan}
                value="subscriptionPayment.PROMOTION_CONTEXT"
                labelLink="subscriptionPayment.PROMOTION_CONTEXT_LINK"
              />
            )}
          </div>

          <ButtonGroup
            userType={userType}
            isBasicOrLitePlan={isBasicOrLitePlan}
            setIsShow={setIsShowViewPlan}
            setIsShowPopupTrialNow={setIsShowPopupTrialNow}
            isTrialPlan={isTrialPlan}
            trialExpired={subscriptionInfo?.trialExpired}
            setIsShowPopupCancel={setIsShowPopupCancel}
            additionNewSubscriptionData={additionNewSubscriptionData}
            setOpenPopupExistedSubscription={setOpenPopupExistedSubscription}
          />
        </div>
      </Element>
      <PopupUpgrade isShow={isShowViewPlan} setIsShow={setIsShowViewPlan} />
      <PopupTrialNow
        isShow={isShowPopupTrialNow}
        setIsShow={setIsShowPopupTrialNow}
      />
      <PopupExistedSubscription
        isShow={openPopupExistedSubscription}
        setIsShow={setOpenPopupExistedSubscription}
        additionNewSubscriptionData={additionNewSubscriptionData}
      />
      <PopupContactUs
        isOpen={isOpenPopupContactUs}
        onClose={onClosePopupContactUs}
      />
      <PopupCancel
        isShow={isShowPopupCancel}
        setIsShow={setIsShowPopupCancel}
      />
    </>
  )
}

const ButtonGroup = ({
  userType,
  isBasicOrLitePlan,
  setIsShow,
  setIsShowPopupTrialNow,
  isTrialPlan,
  trialExpired,
  setIsShowPopupCancel,
  additionNewSubscriptionData,
  setOpenPopupExistedSubscription,
}) => {
  const handleUpgradeNow = (isDisable, additionNewSubscriptionData) => {
    if (isDisable) return
    setIsShow(isDisable)
    if (!additionNewSubscriptionData?.isAvailable) {
      setOpenPopupExistedSubscription(true)
      setIsShow(false)
    } else {
      setIsShow(true)
    }
  }

  const handleTrialNow = () => {
    setIsShowPopupTrialNow(true)
  }

  const handleCancelTrial = () => {
    setIsShowPopupCancel(true)
  }

  const ButtonUpgrade = ({ isDisable }) => {
    return (
      <Button
        action={() => handleUpgradeNow(isDisable, additionNewSubscriptionData)}
        isI18n
        label="subscriptionPayment.UPGRADE_NOW"
        style={{
          border: 'none',
          backgroundColor: '#E8B714',
          opacity: isDisable ? 0.6 : 'unset',
        }}
      />
    )
  }

  return (
    <div className={['dFlex justifyContentEnd gap1', classes.footer].join(' ')}>
      {(!isBasicOrLitePlan && !isTrialPlan) || isTrialPlan ? (
        <Tooltip
          tooltipContent={
            isTrialPlan
              ? 'subscriptionPayment.TOOLTIP_UPGRADE_TRIAL'
              : 'subscriptionPayment.TOOLTIP_UPGRADE'
          }
          isI18n
          position={positionDropdown.TOP_CENTER}
          zIndex={9999}
        >
          <ButtonUpgrade isDisable />
        </Tooltip>
      ) : (
        <ButtonUpgrade />
      )}
      {userType && isBasicOrLitePlan && !trialExpired && (
        <Button
          action={handleTrialNow}
          isI18n
          label="subscriptionPayment.TRIAL_NOW"
          style={{
            border: 'none',
            backgroundColor: '#2C73E8',
            color: '#FFFFFF',
          }}
        />
      )}
      {userType && isTrialPlan && (
        <Button
          action={handleCancelTrial}
          isI18n
          label="subscriptionPayment.CANCEL_TRIAL"
          style={{
            border: 'none',
            backgroundColor: '#2C73E8',
            color: '#FFFFFF',
          }}
        />
      )}
    </div>
  )
}
